import React from 'react';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';

const PlayerManagementTable = ({ players, onEdit, onDelete, loading }) => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
      age--;
    }
    return age;
  };

  const formatPosition = (position) => {
    if (!position) return 'N/A';
    return position?.split('-')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ');
  };

  const formatMarketValue = (value, currency = 'EUR') => {
    if (!value) return 'N/A';
    return `€${parseFloat(value)?.toFixed(1)}M`;
  };

  if (!players || players?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Players Found</h3>
        <p className="text-muted-foreground mb-4">Start by creating your first player profile</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Player
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Position
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Age
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Current Club
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Market Value
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {players?.map((player) => (
              <tr key={player?.id} className="hover:bg-muted/50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                      <Icon name="User" size={20} color="var(--color-muted-foreground)" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{player?.fullName}</div>
                      <div className="text-sm text-muted-foreground">{player?.nationalityPrimary}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-foreground">{formatPosition(player?.primaryPosition)}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-foreground">{calculateAge(player?.dateOfBirth)}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">{player?.currentClub || 'Free Agent'}</div>
                  <div className="text-xs text-muted-foreground">{player?.currentLeague}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-primary">
                    {formatMarketValue(player?.currentMarketValue, player?.currency)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col gap-1">
                    {player?.verifiedStatus && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded">
                        <Icon name="ShieldCheck" size={12} />
                        Verified
                      </span>
                    )}
                    {player?.fifaVerified && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning text-xs font-medium rounded">
                        <Icon name="Award" size={12} />
                        FIFA
                      </span>
                    )}
                    {player?.availableForTransfer && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-accent/10 text-accent text-xs font-medium rounded">
                        <Icon name="TrendingUp" size={12} />
                        Available
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => onEdit(player)}
                          className="text-primary hover:text-primary/80 transition-colors"
                          title="Edit player"
                        >
                          <Icon name="Edit" size={18} />
                        </button>
                        <button
                          onClick={() => onDelete(player?.id)}
                          className="text-error hover:text-error/80 transition-colors"
                          title="Delete player"
                        >
                          <Icon name="Trash2" size={18} />
                        </button>
                      </>
                    )}
                    {!isAdmin && (
                      <span className="text-muted-foreground text-xs">View Only</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlayerManagementTable;